import {Testimonial, TestimonialGroup, Text} from '@primer/react-brand/esm'

import {getLocale} from '@github-ui/swp-core/lib/utils/localization'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'

import Styles from './Testimonials.module.css'

import {useAnimatedMetrics} from '../../../hooks/use-animated-metrics'
import type {Metric, VisualAsset} from '../../../Index.types'

export interface TestimonialsProps {
  a11y: {
    title: string
    testimonialGroup: {
      controlsPrev: string
      controlsNext: string
      controlsNavigation: string
      carouselRoleDescription: string
      slideRoleDescription: string
      indicator: string
      slide: string
      announcement: string
    }
  }
  items: Array<{
    quote: string
    author: {
      name: string
      position: string
      avatar: VisualAsset
    }
  }>
  stats: [Metric, Metric]
}

export const defaultProps: Partial<TestimonialsProps> = {}

export const Testimonials = (props: TestimonialsProps) => {
  const initializedProps: TestimonialsProps = {...defaultProps, ...props}
  const {a11y, items, stats} = initializedProps
  const prefersReducedMotion = usePrefersReducedMotion()

  const getCounterRef = useAnimatedMetrics(stats, getLocale(), prefersReducedMotion)

  return (
    <div className={Styles.Testimonials}>
      <h2 className="visually-hidden">{a11y.title}</h2>
      <div className={Styles.Testimonials__quotes}>
        <TestimonialGroup aria-label={a11y.title} labels={a11y.testimonialGroup}>
          {items.map(({quote, author}) => (
            <Testimonial key={quote} variant="minimal" size="large" quoteMarkColor="green">
              <Testimonial.Quote>{quote}</Testimonial.Quote>
              <Testimonial.Avatar src={author.avatar.url} alt="" width={48} height={48} loading="lazy" />
              <Testimonial.Name position={author.position}>{author.name}</Testimonial.Name>
            </Testimonial>
          ))}
        </TestimonialGroup>
      </div>
      <div className={Styles.Testimonials__stats}>
        {stats.map(stat => (
          <div className={Styles.Testimonials__stat} key={stat.label}>
            <Text as="p" size="600" font="monospace" hasAntiAliasing={false} className={Styles.Testimonials__statValue}>
              <span>{stat.value}</span>
              <span ref={getCounterRef(stat)} aria-hidden="true">
                {stat.value}
              </span>
            </Text>
            <Text as="p" size="100" font="monospace" variant="muted">
              {stat.label}
            </Text>
          </div>
        ))}
      </div>
    </div>
  )
}
